import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ChevronRightIcon from "src/icons/ChevronRight";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import * as Yup from "yup";
import FormikForm, { FormikStep } from "src/components/ui-components/FormikForm";
import InputField from "src/components/ui-components/InputField";
import AlertMessage from "src/components/ui-components/AlertMessage";
import { GetpasswordPolicies, InsUpdPasswordPolicy } from "./ConnectionService";
import { Helmet } from "react-helmet-async";

type pPolicy = {
  passwordPolicyKey?: string | null;
  minPasswordLength?: string;
  maxPasswordLength?: string;
  minDigitCount?: string | null;
  minUpperCaseCharCount?: string;
  minLowerCaseCharCount?: string;
  minSpecialCharCount?: string;
  allowedSpecialChars?: string;
  maxInvalidAttempts?: string;
  specialCharacterCricteriaMessage?: string;
  isPasswordResetRequireInaPeriode?: boolean;
  timePeriodtoKeepPasswordActive?: string;
  isPasswordReUseAllow?: boolean;
  noOfLastPasswwordtoNotReuse?: string
};
const defaultForm: pPolicy = {
  passwordPolicyKey: null,
  minPasswordLength: "",
  maxPasswordLength: "",
  minDigitCount: "",
  minUpperCaseCharCount: "",
  minLowerCaseCharCount: "",
  minSpecialCharCount: "",
  allowedSpecialChars: "",
  maxInvalidAttempts: "",
  specialCharacterCricteriaMessage: "",
  isPasswordResetRequireInaPeriode: null,
  timePeriodtoKeepPasswordActive: "",
  isPasswordReUseAllow: null,
  noOfLastPasswwordtoNotReuse: "",

};


const PasswordPolicyTable = () => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPasswordResetRequireInaPeriode, setisPasswordResetRequireInaPeriode] = useState<boolean>(false);

  const theme = useTheme();
  const formRef = useRef(null);
  const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data, },
      });
    }
  };

  const changeIsPasswordResetRequiredwithindays = (event) => {
    if (event.target.checked) {
      formRef?.current?.setFieldValue('isPasswordResetRequireInaPeriode', event.target.checked);
      setisPasswordResetRequireInaPeriode(event.target.checked);
    } else {
      formRef?.current?.setFieldValue('isPasswordResetRequireInaPeriode', event.target.checked);
      setisPasswordResetRequireInaPeriode(event.target.checked);
    }
  };

  useEffect(() => {
    setIsFetching(true);
    // fetchLanguageList()

    GetpasswordPolicies()
      .then((passPolicy: pPolicy) => {
        resetFormData({
          passwordPolicyKey: passPolicy.passwordPolicyKey,
          minPasswordLength: passPolicy.minPasswordLength,
          maxPasswordLength: passPolicy.maxPasswordLength,
          minDigitCount: passPolicy.minDigitCount,
          minUpperCaseCharCount: passPolicy.minUpperCaseCharCount,
          minLowerCaseCharCount: passPolicy.minLowerCaseCharCount,
          minSpecialCharCount: passPolicy.minSpecialCharCount,
          allowedSpecialChars: passPolicy.allowedSpecialChars,
          maxInvalidAttempts: passPolicy.maxInvalidAttempts,
          specialCharacterCricteriaMessage: "",
          isPasswordResetRequireInaPeriode: passPolicy.isPasswordResetRequireInaPeriode,
          timePeriodtoKeepPasswordActive: passPolicy.timePeriodtoKeepPasswordActive,
          isPasswordReUseAllow: passPolicy.isPasswordReUseAllow,
          noOfLastPasswwordtoNotReuse: passPolicy.noOfLastPasswwordtoNotReuse
        });
        setisPasswordResetRequireInaPeriode(passPolicy.isPasswordResetRequireInaPeriode);
      })
      .catch(() => {
        AlertMessage({ type: "error", msg: "Failed to get data" });
      });
    setIsFetching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Password Policy</title>
      </Helmet>
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid
              alignItems="center"
              sx={{ my: 2 }}
              container
              justifyContent="space-between"
            >
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textSecondary" variant="h5">
                    Password Policy
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item>
                <Grid alignItems="center" container spacing={2}>
                  <Grid item>
                    <Button
                      sx={{
                        background:
                          "linear-gradient(to bottom, #072f40, #072f40)",
                        minWidth: 100,
                      }}
                      component={RouterLink}
                      to=""
                      onClick={() => {
                        formRef?.current?.submitForm()
                      }}
                      disabled={isLoading}
                      variant="contained"
                    >
                      Save
                      {isLoading && (
                        <CircularProgress
                          color="inherit"
                          thickness={5}
                          size={20}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <CardContent>
              {isFetching && <CustomSkeleton skeletonType="FormEdit" />}
              <Box sx={{ display: isFetching ? "none" : "" }}>
                <FormikForm
                  innerRef={formRef}
                  initialValues={defaultForm}
                  onSubmit={async (values): Promise<void> => {
                    setIsLoading(true);
                    await InsUpdPasswordPolicy(values);
                    setIsLoading(false);
                  }}
                >
                  <FormikStep
                    validationSchema={Yup.object({
                      maxPasswordLength: Yup.number()
                        .required("Maximum length of password required")
                        .nullable()
                        .typeError("Maximum length must be a number")
                        .min(1, "Maximum length should be a number and greater than zero"),
                      minPasswordLength: Yup.number()
                        .nullable()
                        .required("Minimum length of password required")
                        .typeError("Minimum length must be a number")
                        .min(1, "Minimum length must be greater than 0"),
                      minUpperCaseCharCount: Yup.number()
                        .nullable()
                        .required("Minimum uppercase character is required")
                        .typeError("Maximum length must be a number")
                        .min(0, "Minimum uppercase character count must be a non negative number")
                        .test(
                          "minUpperCaseLessThanMaxLength",
                          "Minimum uppercase character count cannot be greater than maximum password length",
                          function (value) {
                            const { maxPasswordLength } = this.parent;
                            if (value === null || value === undefined || value === 0) {
                              return true; // Skip validation for empty/null values
                            }
                            return value <= maxPasswordLength;
                          }
                        ),
                      minLowerCaseCharCount: Yup.number()
                        .nullable()
                        .required("Minimum lowercase character is required")
                        .typeError("Maximum length must be a number")
                        .min(0, "Minimum lowercase character count must be a non negative number")
                        .test(
                          "minlowercaseLessThanMaxLength",
                          "Minimum lowercase character count cannot be greater than maximum password length",
                          function (value) {
                            const { maxPasswordLength } = this.parent;
                            if (value === null || value === undefined || value === 0) {
                              return true; // Skip validation for empty/null values
                            }
                            return value <= maxPasswordLength;
                          }
                        ),
                      minDigitCount: Yup.number()
                        .nullable()
                        .required("Minimum digit count is required")
                        .typeError("Minimum digit must be a number")
                        .min(0, "Minimum digit count must be a non negative number")
                        .transform((value) => (value === "" || value === undefined ? 0 : value))
                        .test(
                          "minDigitCountLessThanMaxLength",
                          "Minimum digit count cannot be greater than maximum password length",
                          function (value) {
                            const { maxPasswordLength } = this.parent;
                            if (value === null || value === undefined || value === 0) {
                              return true; // Skip validation for empty/null values
                            }
                            return value <= maxPasswordLength;
                          }
                        ),
                      minSpecialCharCount: Yup.number()
                        .nullable()
                        .required("Password require special character")
                        .typeError("Minimum special character count must be a number")
                        .min(0, "Minimum special character count must be a non negative number")
                        .test(
                          "minSpecialCharCountLessThanMaxLength",
                          "Minimum special character count cannot be greater than maximum password length",
                          function (value) {
                            const { maxPasswordLength } = this.parent;
                            if (value === null || value === undefined || value === 0) {
                              return true; // Skip validation for empty/null values
                            }
                            return value <= maxPasswordLength;
                          }
                        ),
                      maxInvalidAttempts: Yup.number()
                        .nullable()
                        .required("Maximum invalid attempt count is required")
                        .typeError("Maximum invalid attempt must be a number")
                        .min(0, "Maximum invalid attempt must be a non negative number"),
                      allowedSpecialChars: Yup.string()
                        .matches(
                          /^[^a-zA-Z0-9]*$/,
                          "Only special characters are allowed"
                        ),
                      timePeriodtoKeepPasswordActive: Yup.number()
                        .nullable()
                        .typeError("This field must be a number")
                        .min(0, "This field must be a non negative number"),
                      noOfLastPasswwordtoNotReuse: Yup.number()
                        .nullable()
                        .typeError("This field must be a number")
                        .min(0, "This field must be a non negative number"),
                    })}
                  >
                    <Grid spacing={2} container>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>

                        <InputField label="Maximum length of password" name="maxPasswordLength" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Minimum length of Password" name="minPasswordLength" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Minimum digit count" name="minDigitCount" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Minimum uppercase character" name="minUpperCaseCharCount" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Minimum lowercase character" name="minLowerCaseCharCount" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Minimum special character" name="minSpecialCharCount" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Allowed special characters" name="allowedSpecialChars" />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <InputField label="Maximum invalid attempts" name="maxInvalidAttempts" />
                      </Grid>

                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        {/* <SwitchField label="Is a password reset required within a certain duration?"
                          name="isPasswordResetRequireInaPeriode" labelPlacement='start'
                          checked={isPasswordResetRequireInaPeriode}
                          onClick={changeIsPasswordResetRequiredwithindays}
                        /> */}

                        <FormControlLabel
                          control={
                            <Switch
                              //value={isPasswordResetRequireInaPeriode}
                              checked={isPasswordResetRequireInaPeriode}
                              onChange={(event) => changeIsPasswordResetRequiredwithindays(event)}

                            />
                          }
                          label="Is a password reset required within a certain duration?"
                          labelPlacement='start'
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={12} md={6} lg={6} xl={6} >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isPasswordReUseAllow}
                              onChange={(event) => changeIsPasswordReUseAllow(event)}
                            />
                          }
                          label="Is password reuse allowed for this? "
                          labelPlacement='start'
                        />


                      </Grid> */}
                      {isPasswordResetRequireInaPeriode && (
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <InputField label="Number of days to reset the password" name="timePeriodtoKeepPasswordActive" />
                        </Grid>
                      )}
                      {/* {isPasswordReUseAllow && (
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                          <InputField label="Number of times to check if the password was used in the last login attempts." name="noOfLastPasswwordtoNotReuse" />
                        </Grid>
                      )} */}
                    </Grid>
                  </FormikStep>
                </FormikForm>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};
export default PasswordPolicyTable;
